import { generatePath } from 'react-router-dom'
import { ViewType } from '../assets/constants'
import ROUTES from '../assets/routes'
import { Exchange, exchangeFromJSON, exchangeToJSON } from '../proto/generated/common'
import { Security } from '../proto/generated/security'
import { Snapshot } from '../reducer/security.reducer'
import { fetchSecurities } from '../services/apis/securities'
import { parseDDMMMyyDate } from './dateTime'
import { shortMonthNames } from './general'

/** Internal use only - reads from cache synchronously. Use getSecurityAsync for async fetch. */
const getSecurityFromCache = (key: string): Security | undefined => {
    return window.securityManager.getSecurity(key)
}

export type PrimarySecurity = {
    key: string
    displayName?: string
    primary?: Security
    expressionMode: boolean
    expressionSecurities: Security[]
    underlying?: Security
    underlyingSecurityCode: string
}

/** Resolves primary security by fetching all needed securities and building the resolved object. */
export const resolvePrimarySecurity = async (payload: {
    key: string
    displayName?: string
    expressionMode: boolean
    securities: string[]
    underlyingSecurityCode: string
}): Promise<PrimarySecurity> => {
    const { key, displayName, expressionMode, securities: stateSecurities, underlyingSecurityCode } = payload
    const keysToFetch: string[] = [key]
    if (expressionMode && stateSecurities?.length) {
        keysToFetch.push(...stateSecurities)
        if (!underlyingSecurityCode && stateSecurities[0]) {
            const firstSec = await getSecurityAsync(stateSecurities[0])
            if (firstSec) {
                const underlyingCode = firstSec.hasFo ? firstSec.code : firstSec.underlyingSecurityCode
                if (underlyingCode) keysToFetch.push(getSecurityKey(firstSec.exchange, underlyingCode))
            }
        }
    }
    if (underlyingSecurityCode) keysToFetch.push(underlyingSecurityCode)
    await Promise.all([...new Set(keysToFetch)].filter(Boolean).map((k) => getSecurityAsync(k)))

    const primary = expressionMode ? null : getSecurityFromCache(key)
    const expressionSecurities = expressionMode ? stateSecurities.map((t) => getSecurityFromCache(t)).filter((v): v is Security => !!v) : []
    const underlyingKey = underlyingSecurityCode ?? (primary ? getSecurityKey(primary.exchange, primary.underlyingSecurityCode ?? '') : '')
    let underlying: Security | undefined = underlyingKey ? getSecurityFromCache(underlyingKey) : undefined
    if (expressionMode && !underlying && expressionSecurities[0]) {
        const firstSecurityUnderlyingCode = expressionSecurities[0].hasFo ? expressionSecurities[0].code : expressionSecurities[0].underlyingSecurityCode
        if (firstSecurityUnderlyingCode && expressionSecurities.every((s) => (s.hasFo && s.code === firstSecurityUnderlyingCode) || (!s.hasFo && s.underlyingSecurityCode === firstSecurityUnderlyingCode))) {
            underlying = getSecurityFromCache(getSecurityKey(expressionSecurities[0].exchange, firstSecurityUnderlyingCode))
        }
    }
    return {
        key,
        primary: primary ?? undefined,
        displayName,
        expressionMode,
        expressionSecurities,
        underlying,
        underlyingSecurityCode: underlyingSecurityCode ?? (primary ? getSecurityKey(primary.exchange, primary.underlyingSecurityCode ?? '') : '')
    }
}

export const getSecurityKey = (exchange: Exchange, code: string) => `${exchangeToJSON(exchange)}:${code}`

/** Suffix on seriesGroup.securityList[0] so duplicate compare symbols get distinct series groups; strip before data/cache keys. */
export const COMPARABLE_INSTANCE_DELIM = '||cmp||'

export const getComparableSeriesGroupToken = (baseSecurityKey: string, seriesGroupId: string) =>
    `${baseSecurityKey}${COMPARABLE_INSTANCE_DELIM}${seriesGroupId}`

export const getBaseSecurityKeyFromComparableToken = (token: string): string => {
    const i = token.indexOf(COMPARABLE_INSTANCE_DELIM)
    return i === -1 ? token : token.slice(0, i)
}

export const getSecuritySymbol = (exchange: Exchange, symbol: string) => `${exchangeToJSON(exchange)}:${symbol}`

export const getExpressionSecurityKey = (exchange: Exchange, code: string) => `${exchangeToJSON(exchange)}__${code}`

export const getExchangeAndCodeFromKey = (key: string): [Exchange, string] => {
    const base = getBaseSecurityKeyFromComparableToken(key)
    let [exchange, code] = base.split(':')
    return [exchangeFromJSON(exchange) as Exchange, code]
}

export const getSecurityAsync = async(key: string) => {
    const regex = /^[a-zA-Z]+:[a-zA-Z0-9_]+$/
    let security = getSecurityFromCache(key)
    if (!security && regex.test(key)) {
        const [exchange, code] = getExchangeAndCodeFromKey(key)
        // Native embedded (iOS): use async bridge; Android uses sync AndroidDataBridge in getSecurity
        const { isNativeEmbedded, getSecurityFromNative } = await import('./nativeBridge')
        if (isNativeEmbedded()) {
            const securityString = await getSecurityFromNative(exchange.toString(), code)
            if (securityString) {
                const binaryString = atob(securityString)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                const { Security } = await import('../proto/generated/security')
                security = Security.decode(bytes)
                if (security?.code) {
                    window.securityManager.setSecurity(security)
                    return security
                }
            }
        }
        // Web or Android: fetch from API (web) or getSecurity already tried sync (Android)
        const securities = await fetchSecurities([{ exchange, code }])
        if (securities.length) {
            securities.forEach(sec => window.securityManager.setSecurity(sec))
            security = securities[0]
        }
    }
    return security
}

export const getExchangeAndSymbolFromKey = (key: string): [Exchange, string] => {
    let [exchange, symbol] = key.includes(':') ? key.split(':') : [Exchange.NSE, key]
    let finalExchange = exchangeFromJSON(exchange)
    if([Exchange.EXCHANGE_UNKNOWN, Exchange.UNRECOGNIZED].includes(finalExchange)) {
        finalExchange = Exchange.NSE
    }
    return [finalExchange, symbol]
}


export const isValidExchange = (exchange: Exchange | undefined): boolean => {
    return (exchange !== undefined && exchange !== Exchange.EXCHANGE_UNKNOWN && exchange !== Exchange.UNRECOGNIZED)
}

export const calculateSnapshotDetails = (sec: Security, sn: Snapshot): {
    ltp: number,
    ltpChange: number,
    changePercentage: number,
    volume: number,
    referencedSecurity?: Security | undefined;
} | null => {
    if (!sn || !sec) {
        return null
    }
    const priceMultiplier = sec.priceMultiplier || 100
    const change = sn.lastTradedPrice / priceMultiplier - sn.previousClose / priceMultiplier
    const previousClose = sn.previousClose / priceMultiplier
    const volume = sn.volume
    const ltp = sn.lastTradedPrice / priceMultiplier
    const changePercentage = change * 100 / previousClose
    return {
        ltp: ltp,
        ltpChange: change,
        changePercentage: changePercentage,
        volume: volume,
        referencedSecurity: sn.referencedSecurity
    }
}

export const parseDetailsFromOptionSecurityCode = (securityCode: string, priceMultiplier: number = 100) => {
    if (!securityCode.length) return undefined
    const splits = securityCode.split('_').reverse()
    const expiryDate = parseDDMMMyyDate(splits[2])
    return {
        expiryName: expiryDate ? `${expiryDate.getDate()} ${shortMonthNames[expiryDate.getMonth()]}` : undefined,
        strike: Number(splits[1]) / priceMultiplier
    }

}

export const parseDetailsFromFutureSecurityCode = (securityCode: string) => {
    if (!securityCode.length) return undefined
    const splits = securityCode.split('_').reverse()
    const expiryDate = parseDDMMMyyDate(splits[0])
    return {
        expiryName: expiryDate ? `${expiryDate.getDate()} ${shortMonthNames[expiryDate.getMonth()]}` : undefined
    }
}

export const isFnOSecurity = (security: Security): void | Boolean => {
    return !!(security?.underlyingSecurityCode && security?.underlyingSecurityCode !== security?.code)
}

export const getSecuritiesFromSecuritiesObj = async (securities: Security[]): Promise<Security[] | undefined> => {
    const securitiesToFetch = securities.filter(v => !getSecurityFromCache(getSecurityKey(v.exchange, v.code)))
    if (securitiesToFetch.length) {
        try {
            const response = await fetchSecurities(securities.map((v) => ({
                exchange: v.exchange,
                code: v.code
            })))

            const secs = response
            response.forEach((v) => {
                window.securityManager.setSecurity(v)
            });
            return secs
        } catch (e) {
            console.error(e)
            return undefined
        }
    }
    return securities.map((v) => getSecurityFromCache(getSecurityKey(v.exchange, v.code)))
}

export const getPathForSecurity = (viewType: ViewType, security: Security): string => {
    switch (viewType) {
        case ViewType.OPTION_CHAIN:
            return generatePath(ROUTES.optionChain, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.STRATEGY_BUILDER:
            return generatePath(ROUTES.strategyBuilder, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.OPEN_INTEREST:
            return generatePath(ROUTES.openInterest, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.OPEN_INTEREST_CHANGE:
            return generatePath(ROUTES.openInterestChange, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.MULTI_STRIKES_CHART:
            return generatePath(ROUTES.multiStrikeChart, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        case ViewType.SIMULATOR:
            return generatePath(ROUTES.simulator, {
                exchange: exchangeToJSON(security.exchange),
                symbol: security.code
            })
        default:
            return '#'
    }
}